/**
 * Single source of truth for the password policy enforced by Supabase Auth
 * on this project.
 *
 * Configured in the Supabase dashboard at:
 *   Authentication > Policies > Password Requirements
 * Current setting: "Lowercase, uppercase letters, digits and symbols",
 *                  minimum length 8.
 *
 * The constants and validator below MUST mirror that dashboard setting.
 * If the dashboard policy is changed, update this file so the client-side
 * placeholder, helper text, and pre-submit validation match what the
 * server will accept. Keeping them in lockstep is what prevents bugs like
 * #732, where the UI claimed "letters and numbers" while the server
 * actually demanded all four character classes.
 *
 * Note on the symbol class: GoTrue treats any non-alphanumeric character
 * as a symbol, so we mirror that with `[^A-Za-z0-9]`. This is intentionally
 * permissive — if Supabase ever tightens its symbol set, the worst case is
 * the client passes validation and the server rejects, falling through to
 * the friendly error message.
 *
 * Pre-compiled to JS (with companion .d.ts) so CRA's webpack can consume
 * this cross-workspace module without a TS loader. Matches the pattern
 * used by agentLog.js in this directory.
 */

const PASSWORD_MIN_LENGTH = 8;

const PASSWORD_RULES = [
  {
    id: 'length',
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    test: (pw) => pw.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'lowercase',
    label: 'A lowercase letter (a–z)',
    test: (pw) => /[a-z]/.test(pw),
  },
  {
    id: 'uppercase',
    label: 'An uppercase letter (A–Z)',
    test: (pw) => /[A-Z]/.test(pw),
  },
  {
    id: 'digit',
    label: 'A digit (0–9)',
    test: (pw) => /[0-9]/.test(pw),
  },
  {
    id: 'symbol',
    label: 'A symbol (e.g. ! @ # $ %)',
    test: (pw) => /[^A-Za-z0-9]/.test(pw),
  },
];

const PASSWORD_POLICY_DESCRIPTION =
  `Password must be at least ${PASSWORD_MIN_LENGTH} characters and include a lowercase letter, an uppercase letter, a digit, and a symbol.`;

const PASSWORD_POLICY_PLACEHOLDER =
  `${PASSWORD_MIN_LENGTH}+ chars, upper & lower, digit, symbol`;

function validatePassword(pw) {
  const failed = PASSWORD_RULES.filter((rule) => !rule.test(pw));
  if (failed.length === 0) return { ok: true };

  const labels = failed.map((r) => r.label.toLowerCase());
  const reason =
    labels.length === 1
      ? `Password is missing: ${labels[0]}.`
      : `Password is missing: ${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}.`;

  return { ok: false, failed: [...failed], reason };
}

module.exports = {
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULES,
  PASSWORD_POLICY_DESCRIPTION,
  PASSWORD_POLICY_PLACEHOLDER,
  validatePassword,
};
